import { useQuery } from '@tanstack/react-query';

import { metasApi, metasKeys } from '../metas.api';
import type { GoalsFilters } from '../metas.types';

/** Listado de metas con filtros. */
export function useMetas(filtros: GoalsFilters, enabled = true) {
  return useQuery({
    queryKey: metasKeys.lista(filtros),
    queryFn: ({ signal }) => metasApi.listar(filtros, signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Panel principal de metas. */
export function useMetasDashboard(enabled = true) {
  return useQuery({
    queryKey: metasKeys.dashboard,
    queryFn: ({ signal }) => metasApi.dashboard(signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Reportes estadísticos. */
export function useMetasReportes(enabled = true) {
  return useQuery({
    queryKey: metasKeys.reportes,
    queryFn: ({ signal }) => metasApi.reportes(signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Detalle de una meta. */
export function useMeta(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.detalle(id ?? 0),
    queryFn: ({ signal }) => metasApi.detalle(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Avance recalculado de la meta. */
export function useMetaProgreso(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.progreso(id ?? 0),
    queryFn: ({ signal }) => metasApi.progreso(id as number, signal),
    enabled: enabled && Boolean(id),
    staleTime: 30_000,
  });
}

/** Serie para el gráfico de la meta. */
export function useMetaGrafico(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.grafico(id ?? 0),
    queryFn: ({ signal }) => metasApi.grafico(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Comparativo contra el período anterior. */
export function useMetaComparativo(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.comparativo(id ?? 0),
    queryFn: ({ signal }) => metasApi.comparativo(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Consolidado diario de la meta. */
export function useMetaConsolidado(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.consolidado(id ?? 0),
    queryFn: ({ signal }) => metasApi.consolidado(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Evaluaciones registradas. */
export function useMetaEvaluaciones(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.evaluaciones(id ?? 0),
    queryFn: ({ signal }) => metasApi.evaluaciones(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Historial de cambios de la meta. */
export function useMetaHistorial(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.historial(id ?? 0),
    queryFn: ({ signal }) => metasApi.historial(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Bitácora de auditoría de la meta. */
export function useMetaAuditoria(id: number | null, enabled = true) {
  return useQuery({
    queryKey: metasKeys.auditoria(id ?? 0),
    queryFn: ({ signal }) => metasApi.auditoria(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Configuración del sistema de metas. */
export function useMetasConfiguracion(enabled = true) {
  return useQuery({
    queryKey: metasKeys.configuracion,
    queryFn: ({ signal }) => metasApi.configuracion(signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Catálogo de productos (grados de combustible). */
export function useMetaProductos(enabled = true) {
  return useQuery({
    queryKey: metasKeys.productos,
    queryFn: ({ signal }) => metasApi.productos(signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Catálogo de lados. */
export function useMetaLados(enabled = true) {
  return useQuery({
    queryKey: metasKeys.lados,
    queryFn: ({ signal }) => metasApi.lados(signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Catálogo de usuarios (para asignar responsables). */
export function useMetaUsuarios(enabled = true) {
  return useQuery({
    queryKey: metasKeys.usuarios,
    queryFn: ({ signal }) => metasApi.usuarios(signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}
